import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { haalMarkt, type MarktRol } from '../../services/professionalDataApi';
import { Kaart, Laden } from '../data/DataFilterBalk';
import { REEKS_KLEUREN, euro } from '../data/DataCharts';
import { KolomGrafiek } from './Grafieken';
import { FilterRij, Fout, Grendel, Keuze, jaarOpties } from './Bediening';

export const MarktOnderdeel: React.FC = () => {
  const { t } = useTranslation('professionalData');
  const nu = new Date();
  const [rol, setRol] = useState<MarktRol>('VTP');
  const [jaar, setJaar] = useState(nu.getFullYear());
  const [kwartaal, setKwartaal] = useState(Math.floor(nu.getMonth() / 3) + 1);
  const q = useQuery({ queryKey: ['professional-data', 'markt', rol, jaar, kwartaal], queryFn: () => haalMarkt(rol, jaar, kwartaal), placeholderData: keepPreviousData });
  const d = q.data;
  const onderdruktLabel = d ? t('markt.onderdrukt', { min: d.minimum_professionals }) : '';

  return (
    <div className="space-y-6">
      <FilterRij>
        <Keuze
          label={t('markt.rol')}
          waarde={rol}
          onChange={setRol}
          opties={(['VTP', 'BMW', 'VITALITEIT'] as MarktRol[]).map((r) => ({ waarde: r, label: t(`markt.${r}`) }))}
        />
        <Keuze label={t('markt.kwartaal')} waarde={kwartaal} onChange={setKwartaal} opties={[1, 2, 3, 4].map((k) => ({ waarde: k, label: `Q${k}` }))} />
        <Keuze label={t('markt.jaar')} waarde={jaar} onChange={setJaar} opties={jaarOpties()} />
      </FilterRij>

      {q.isLoading && <Laden />}
      {q.isError && <Fout fout={q.error} />}
      {d && (
        <Kaart titel={t('markt.grafiekTitel', { rol: t(`markt.${d.rol}`), kwartaal: d.kwartaal, jaar: d.jaar })} uitleg={t('markt.grafiekUitleg')}>
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: REEKS_KLEUREN[0] }} />
              {t('markt.mediaan')}
            </span>
            {d.eigen_tarief != null ? (
              <span className="flex items-center gap-1.5">
                <svg width="18" height="6" aria-hidden><line x1="0" y1="3" x2="18" y2="3" stroke="#0B1F3A" strokeWidth="2" strokeDasharray="6 4" /></svg>
                {t('markt.eigenTarief')}: <strong className="text-gray-900">{euro(d.eigen_tarief, 2)}</strong>
              </span>
            ) : (
              <span>{t('markt.geenEigen')}</span>
            )}
            {d.eigen_categorie && <span>· {t('markt.jouwCategorie', { cat: d.eigen_categorie })}</span>}
          </div>
          <KolomGrafiek
            categorieen={d.categorieen.map((c) => ({
              key: c.categorie,
              label: t('markt.jaren', { cat: c.categorie }),
              waarden: { mediaan: c.mediaan },
              sub: c.professionals != null ? `${t('markt.professionals')}: ${c.professionals}` : undefined,
            }))}
            reeksen={[{ key: 'mediaan', label: t('markt.mediaan'), kleur: REEKS_KLEUREN[0] }]}
            formaat={(v) => euro(v)}
            referentie={d.eigen_tarief != null ? { waarde: d.eigen_tarief, label: t('markt.eigenTarief') } : null}
            onderdruktLabel={onderdruktLabel}
          />
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                  <th className="py-2 pr-3 font-medium">{t('markt.categorie')}</th>
                  <th className="py-2 pr-3 text-right font-medium">{t('markt.mediaan')}</th>
                  <th className="py-2 text-right font-medium">{t('markt.professionals')}</th>
                </tr>
              </thead>
              <tbody>
                {d.categorieen.map((c) => (
                  <tr key={c.categorie} className={`border-b border-gray-100 ${c.categorie === d.eigen_categorie ? 'bg-gold-50' : ''}`}>
                    <td className="py-2 pr-3">{t('markt.jaren', { cat: c.categorie })}</td>
                    <td className="py-2 pr-3 text-right font-medium">{c.mediaan == null ? <span className="inline-flex items-center gap-1 text-xs font-normal text-amber-800"><Lock className="h-3 w-3" aria-hidden />{onderdruktLabel}</span> : euro(c.mediaan, 2)}</td>
                    <td className="py-2 text-right">{c.professionals ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4">
            <Grendel>{t('markt.acm', { min: d.minimum_professionals })}</Grendel>
          </div>
        </Kaart>
      )}
    </div>
  );
};
